import { useEffect } from 'react';
import {
  RegistrationServiceRoot,
  EicInformation,
} from 'src/apis/organisation-registration/types';
import { useAxios } from 'src/hooks/use-axios';
import { isConstraintViolation } from 'src/utils/is-constraint-violation';
import urlTemplate from 'url-template';

export function useOrganisationRegistration() {
  const organisationRegistration = useAxios(
    (axios, signal) =>
      axios.request<RegistrationServiceRoot>({
        signal,
        url: PRISMA_CONFIG.organisationRegistration,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    organisationRegistration.execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return organisationRegistration;
}

export type OrganisationRegistrationRequest = ReturnType<
  typeof useOrganisationRegistration
>;

export function useGetEicInformation(url: string) {
  return useAxios(
    async (axios, signal, eic: string) => {
      const result = await axios.request<EicInformation | null>({
        signal,
        url: urlTemplate.parse(url).expand({ eic }),
        method: 'GET',
        validateStatus: () => true,
      });

      // by setting data to null on an error case, we get better type safety
      if (result.status !== 200) result.data = null;

      return result;
    },
    { neededOnPageLoad: false }
  );
}

export type GetEicInformationRequest = ReturnType<typeof useGetEicInformation>;

export type DvgwParams = {
  dvgwCode: string;
};

export function useGetDvgwInformation(url: string) {
  return useAxios(
    async (axios, signal, { dvgwCode }: DvgwParams) => {
      return await axios.request({
        signal,
        url: urlTemplate.parse(url).expand({
          dvgwCode,
        }),
        validateStatus: (status) => status === 204 || status === 404,
      });
    },
    {
      neededOnPageLoad: false,
      onError(error) {
        if (isConstraintViolation(error)) {
          return error;
        } else {
          throw error;
        }
      },
    }
  );
}

export type GetDvgwInformationRequest = ReturnType<
  typeof useGetDvgwInformation
>;
